import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchCoinNetworks, 
  fetchCoinNetworkById, 
  checkCoinNetwork,
  createCoinNetwork, 
  updateCoinNetwork, 
  deleteCoinNetwork 
} from '../api';

/**
 * Hook for fetching all coin networks
 * @param networkId Optional network ID filter
 * @param coinId Optional coin ID filter
 * @returns Query result with coin networks data
 */
export const useCoinNetworks = (networkId?: string, coinId?: string) => {
  return useQuery({
    queryKey: ['coinNetworks', networkId, coinId],
    queryFn: () => fetchCoinNetworks(networkId, coinId)
  });
};

/**
 * Hook for fetching a coin network by ID
 * @param id Coin network ID
 * @returns Query result with coin network data
 */
export const useCoinNetwork = (id: string) => {
  return useQuery({
    queryKey: ['coinNetwork', id],
    queryFn: () => fetchCoinNetworkById(id),
    enabled: !!id // Only run the query if id is provided
  });
};

/**
 * Hook for checking if a coin network exists
 * @param coinSymbol Coin symbol
 * @param networkTitle Network title
 * @returns Query result with coin network if exists
 */
export const useCheckCoinNetwork = (coinSymbol: string, networkTitle: string) => {
  return useQuery({
    queryKey: ['checkCoinNetwork', coinSymbol, networkTitle],
    queryFn: () => checkCoinNetwork(coinSymbol, networkTitle),
    enabled: !!(coinSymbol && networkTitle) // Only run if both parameters are provided
  });
};

/**
 * Hook for creating a new coin network
 * @returns Mutation for creating a coin network
 */
export const useCreateCoinNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (coinNetworkData: any) => createCoinNetwork(coinNetworkData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coinNetworks'] });
    }
  });
};

/**
 * Hook for updating a coin network
 * @returns Mutation for updating a coin network
 */
export const useUpdateCoinNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, coinNetworkData }: { id: string, coinNetworkData: any }) => 
      updateCoinNetwork(id, coinNetworkData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['coinNetworks'] });
      queryClient.invalidateQueries({ queryKey: ['coinNetwork', variables.id] });
    }
  });
};

/**
 * Hook for deleting a coin network
 * @returns Mutation for deleting a coin network
 */
export const useDeleteCoinNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => deleteCoinNetwork(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coinNetworks'] });
    }
  });
};
